import { useEffect, useMemo, useState } from 'react';
import { Control, UseFormRegister, useWatch } from 'react-hook-form';
import {
  Button,
  Checkbox,
  CheckboxGroup,
  ChevronDownIcon,
  Flex,
  FormControl,
  HStack,
  Menu,
  MenuButton,
  MenuFuseHighlight,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  MenuSearchBox,
  Text,
  VirtualizedList,
} from '@cdl/heartbeat-react';
import { FormValues } from '../../../types/annotation-task-detail.type';
import { Annotator } from '../../../../ecg/types/annotation-tasks.type';
import { DisplaySelectedAnnotators } from './DisplaySelectedAnnotators';
import { useSelector, useDispatch } from 'react-redux';
import { annotatorsListSelector } from '../../../selectors/admin.selectors';
import { loadAnnotatorsList } from '../../../thunks/load-annotators-list.thunks';

type Props = {
  selectedData: Annotator[];
  handleDataChange: (values: Annotator[]) => void;
  register: UseFormRegister<FormValues>;
  control: Control<FormValues>;
};

const checkboxLabel = [
  { label: 'Original', value: 'original' },
  { label: 'Test', value: 'test' },
  { label: 'Alternate', value: 'alternate' },
];

export const DropdownAnnotators: React.FC<Props> = ({
  selectedData,
  handleDataChange,
  register,
  control,
}) => {
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [precedentValue, setIsPrecedentValue] = useState<string[]>([]);
  const [isChecked, setIsChecked] = useState<string[]>([]);
  const [checkedItems, setCheckedItems] = useState<string[]>([]);

  const annotatorsList = useSelector(annotatorsListSelector);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(loadAnnotatorsList(false));
  }, [dispatch]);

  const annotatorsOptions = useMemo(
    () =>
      annotatorsList.map(({ id, name, email }) => ({
        value: id,
        displayValue: name,
        annotator: { id, name, email },
      })),
    [annotatorsList],
  );

  const getDisplayNameById = (id: string) =>
    annotatorsOptions.find((el) => el.annotator.id === id)?.displayValue || '';

  const filterAnnotatorsByEmailPattern = (
    emailContains: string,
    excludes: string[] = [],
  ) =>
    annotatorsOptions
      .filter(
        (x) =>
          x.annotator.email.includes(emailContains) &&
          !excludes.some((exclude) => x.annotator.email.includes(exclude)),
      )
      .map((el) => el.value);

  const ingedataOriginalIds = filterAnnotatorsByEmailPattern('@ingedata.ai', [
    '$test',
    '$alternate',
  ]);
  const ingedataTestIds = filterAnnotatorsByEmailPattern('$test@ingedata.ai');
  const ingedataAlternateIds = filterAnnotatorsByEmailPattern(
    '$alternate@ingedata.ai',
  );

  const categories = [
    { key: 'original', ids: ingedataOriginalIds },
    { key: 'test', ids: ingedataTestIds },
    { key: 'alternate', ids: ingedataAlternateIds },
  ];

  const handleCheckBox = (value: string[]) => {
    const updatedCheckedItems = [...checkedItems];
    let updatedSelectedOptions = [...selectedOptions];

    categories.forEach(({ key, ids }) => {
      const isSelected = value.includes(key);
      const wasSelected = precedentValue.includes(key);

      if (isSelected && !wasSelected) {
        updatedCheckedItems.push(key);
        updatedSelectedOptions = Array.from(
          new Set([...updatedSelectedOptions, ...ids]),
        );
      } else if (!isSelected && wasSelected) {
        updatedSelectedOptions = updatedSelectedOptions.filter(
          (el) => !ids.includes(el),
        );
        if (!containsAll(updatedSelectedOptions, ids)) {
          updatedCheckedItems.splice(updatedCheckedItems.indexOf(key), 1);
        }
      }
    });

    if (!value.some((item) => categories.map((el) => el.key).includes(item))) {
      updatedCheckedItems.length = 0;
    }

    const dataFiltered = annotatorsOptions.filter((el) =>
      updatedSelectedOptions.includes(el.annotator.id),
    );

    setCheckedItems(updatedCheckedItems);
    setSelectedOptions(updatedSelectedOptions);
    handleDataChange(dataFiltered.map((el) => el.annotator));
    setIsPrecedentValue(value);
  };

  function containsAll(arr: string[], target: string[]) {
    return target.every((item) => arr.includes(item));
  }

  const updateCheckedItems = (
    category: string,
    annotators: string[],
    ids: string[],
    checkedItems: string[],
  ) => {
    if (checkedItems.includes(category) && !containsAll(annotators, ids)) {
      return checkedItems.filter((item) => item !== category);
    }
    return checkedItems;
  };

  const handleSelectedDataChange = (optionCodes: string | string[]) => {
    const annotators = Array.isArray(optionCodes) ? optionCodes : [optionCodes];
    let updatedCheckedItems = [...checkedItems];

    updatedCheckedItems = updateCheckedItems(
      'original',
      annotators,
      ingedataOriginalIds,
      updatedCheckedItems,
    );
    updatedCheckedItems = updateCheckedItems(
      'test',
      annotators,
      ingedataTestIds,
      updatedCheckedItems,
    );
    updatedCheckedItems = updateCheckedItems(
      'alternate',
      annotators,
      ingedataAlternateIds,
      updatedCheckedItems,
    );

    if (updatedCheckedItems.length === 0) {
      updatedCheckedItems = [];
      setIsChecked([]);
      setIsPrecedentValue([]);
    }

    setCheckedItems(updatedCheckedItems);
    setSelectedOptions(annotators);

    const dataUpdated = annotatorsOptions.filter((el) =>
      annotators.includes(el.annotator.id),
    );
    handleDataChange(dataUpdated.map((el) => el.annotator));
  };

  const handleDeselectOption = (optionToDeselect: string) => {
    const updatedSelectedOptions = selectedOptions.filter(
      (item) => item !== optionToDeselect,
    );
    const dataUpdated = selectedData.filter(
      (item) => item.id !== optionToDeselect,
    );

    let updatedCheckedItems = [...checkedItems];
    let updatedIsPrecedentValue = [...precedentValue];

    updatedCheckedItems = updateCheckedItems(
      'original',
      updatedSelectedOptions,
      ingedataOriginalIds,
      updatedCheckedItems,
    );
    updatedCheckedItems = updateCheckedItems(
      'test',
      updatedSelectedOptions,
      ingedataTestIds,
      updatedCheckedItems,
    );
    updatedCheckedItems = updateCheckedItems(
      'alternate',
      updatedSelectedOptions,
      ingedataAlternateIds,
      updatedCheckedItems,
    );

    if (updatedCheckedItems.length === 0) {
      updatedCheckedItems = [];
      updatedIsPrecedentValue = [];
      setCheckedItems([]);
      setIsChecked([]);
    }

    setCheckedItems(updatedCheckedItems);
    setIsPrecedentValue(updatedIsPrecedentValue);
    setSelectedOptions(updatedSelectedOptions);
    handleDataChange(dataUpdated);
  };

  const value = useWatch({ control, name: 'annotators' });
  useEffect(() => {
    if (value.length === 0) {
      setSelectedOptions([]);
      setCheckedItems([]);
      setIsPrecedentValue([]);
      setIsChecked([]);
    }
  }, [value]);

  return (
    <>
      <FormControl pb='m'>
        <input
          {...register('annotators', {
            required: 'This is required',
          })}
          style={{ display: 'none' }}
        />
        <Text textStyle='label' color='neutral.300' mb='s'>
          Ingedata
        </Text>
        <CheckboxGroup
          value={checkedItems}
          onChange={(e) => {
            handleCheckBox(e as string[]);
            setIsChecked(e as string[]);
          }}
        >
          <HStack mb='l'>
            {checkboxLabel.map((label) => (
              <Checkbox
                key={label.value}
                value={label.value}
                isDisabled={
                  isChecked.length > 0 && isChecked.toString() !== label.value
                }
              >
                {label.label}
              </Checkbox>
            ))}
          </HStack>
        </CheckboxGroup>
        <Menu closeOnSelect={false} variant='virtual' matchWidth>
          <MenuButton
            as={Button}
            variant='secondary'
            w='full'
            textAlign='left'
            fontWeight='regular'
            rightIcon={<ChevronDownIcon />}
          >
            Select annotators
          </MenuButton>
          <MenuList maxWidth='unset' zIndex={40}>
            <MenuSearchBox options={annotatorsOptions} placeholder='Search...'>
              {(filteredOptions) => (
                <Flex flexDirection='column'>
                  <VirtualizedList
                    rowHeight={35}
                    containerHeight={300}
                    size={filteredOptions.length || annotatorsOptions.length}
                  >
                    {(virtualItems) => (
                      <MenuOptionGroup
                        type='checkbox'
                        value={selectedOptions}
                        onChange={(value) =>
                          handleSelectedDataChange(value as string[])
                        }
                      >
                        {virtualItems.map(({ index, key, style }) => {
                          const option =
                            filteredOptions.length > 0
                              ? filteredOptions[index]
                              : annotatorsOptions[index];
                          const { value, displayValue } = option.item || option;
                          return (
                            <MenuItemOption
                              value={value}
                              key={key}
                              style={style}
                            >
                              {filteredOptions.length > 0 ? (
                                <MenuFuseHighlight
                                  fallback={value}
                                  hit={option}
                                />
                              ) : (
                                displayValue
                              )}
                            </MenuItemOption>
                          );
                        })}
                      </MenuOptionGroup>
                    )}
                  </VirtualizedList>
                </Flex>
              )}
            </MenuSearchBox>
          </MenuList>
        </Menu>
      </FormControl>
      <DisplaySelectedAnnotators
        selectedOptions={selectedOptions}
        getDisplayNameById={getDisplayNameById}
        handleDeselectOption={handleDeselectOption}
      />
    </>
  );
};
